import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { fmtMoney } from "@/lib/api";
import { PageHeader } from "@/components/shared/PageHeader";
import { StatCard } from "@/components/shared/StatCard";
import { TrendingUp, ShoppingCart, ShoppingBag, Package, Users, AlertTriangle, Wallet, BarChart3 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";

export default function StoreDashboard() {
  const { slug } = useParams();
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/store/${slug}/dashboard`).then((r) => setD(r.data)).catch(() => {}); }, [slug]);
  if (!d) return <div className="text-slate-400">Chargement…</div>;

  return (
    <div>
      <PageHeader title="Tableau de bord" subtitle="Performance de votre boutique" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Chiffre d'affaires" value={fmtMoney(d.revenue)} icon={TrendingUp} testid="store-metrics-revenue" />
        <StatCard label="Ventes (POS)" value={d.sales_count} icon={ShoppingCart} />
        <StatCard label="Commandes en ligne" value={d.orders_count} icon={ShoppingBag} />
        <StatCard label="Panier moyen" value={fmtMoney(d.avg_basket)} icon={BarChart3} />
        <StatCard label="Produits vendus" value={d.products_sold} icon={Package} />
        <StatCard label="Clients" value={d.customers} icon={Users} />
        <StatCard label="Charges" value={fmtMoney(d.charges)} icon={Wallet} />
        <StatCard label="Résultat estimé" value={fmtMoney(d.estimated_result)} icon={TrendingUp} hint="CA − charges" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="vp-card rounded-xl p-6 lg:col-span-2">
          <h3 className="text-lg font-semibold text-white mb-4">Ventes des 14 derniers jours</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={d.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
              <XAxis dataKey="date" stroke="#64748b" fontSize={11} tickFormatter={(v) => v?.slice(5)} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={{ background: "#14161D", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }} />
              <Bar dataKey="amount" fill="#3DBB2C" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="space-y-6">
          <div className="vp-card rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Top produits</h3>
            <div className="space-y-3">
              {d.top_products.length === 0 && <p className="text-sm text-slate-500">Aucune vente</p>}
              {d.top_products.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm"><span className="text-slate-200">{p.name}</span><span className="mono text-slate-400">{p.qty}</span></div>
              ))}
            </div>
          </div>
          <div className="vp-card rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2"><AlertTriangle size={16} className="text-amber-400" /> Stock faible</h3>
            <div className="space-y-3">
              {d.low_stock.length === 0 && <p className="text-sm text-slate-500">Stock OK</p>}
              {d.low_stock.map((p, i) => (
                <div key={i} className="flex items-center justify-between text-sm"><span className="text-slate-200">{p.name}</span><span className="mono text-amber-400">{p.qty}</span></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
